import { DOCUMENT, ErrorHandler, Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ToastService } from '../../shared/ui/toast/toast.service';
import { apiErrorMessage, isApiError } from '../auth/api-error';

export const UNEXPECTED_ERROR_MESSAGE = 'Something went wrong. Reload the page and try again.';

const RELOAD_MARKER_KEY = 'me-operations-ui.chunk-reload';

/** A second chunk failure within this window means the reload did not help: stop reloading. */
const RELOAD_LOOP_WINDOW_MS = 30_000;

/**
 * A lazy chunk that fails after a redeploy reloads once, never in a loop. An unhandled API error shows
 * the envelope's message; anything else shows a generic toast.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toasts = inject(ToastService);
  private readonly window = inject(DOCUMENT).defaultView;

  handleError(error: unknown): void {
    if (!environment.production) {
      // Dev only: errors can carry URLs with query strings, so production never logs.
      // eslint-disable-next-line no-console
      console.error(error);
    }

    if (isChunkLoadError(error) && this.reloadOnce()) {
      return;
    }

    this.toasts.error(isApiError(error) ? apiErrorMessage(error) : UNEXPECTED_ERROR_MESSAGE);
  }

  private reloadOnce(): boolean {
    if (this.window === null) {
      return false;
    }

    const now = Date.now();

    try {
      const last = Number(this.window.sessionStorage.getItem(RELOAD_MARKER_KEY));

      if (Number.isFinite(last) && now - last < RELOAD_LOOP_WINDOW_MS) {
        return false;
      }

      this.window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(now));
    } catch {
      // No storage means no loop guard: show the message rather than risk reloading forever.
      return false;
    }

    this.window.location.reload();

    return true;
  }
}

/** A failed `@defer` block load (NG0750: production carries no message), or browser messages for a dynamic `import()` whose file no longer exists. */
export function isChunkLoadError(error: unknown): boolean {
  if ((error as { code?: unknown } | null)?.code === -750) {
    return true;
  }

  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';

  return /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|loading chunk [\w-]+ failed/i.test(
    message,
  );
}
